import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { InlineFormSchemaDialogComponent } from '@dialogs/inline-form-schema-dialog/inline-form-schema-dialog.component';
import { ColorAliases, MdsSizeType, TransformMatrix } from '@mds/common';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { AIValidationStatuses, DividerComponent } from '@mds/ui';
import { AnalyzeFiltersService } from '@services/analyze.filters.service';
import { AnalyzeService } from '@services/analyze.service';
import { EventDelegatorService } from '@services/event-delegator.service';
import { GlobalDataService } from '@services/global-data.service';
import { MakersiteService } from '@services/makersite.service';
import { MenuService } from '@services/menu.service';
import { ReusableService } from '@services/reusable.service';
import { SankeyOthersService } from '@services/sankey-others.service';
import { ToastrService } from '@services/toaster.service';
import * as d3 from 'd3';
import * as d3Sankey from 'd3-sankey';
import _ from 'lodash';
import { fromEvent, Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { saveSvgAsPng } from 'save-svg-as-png';
import { SankyLegend } from './sankey-v2-legend';

@Component({
  selector: 'app-sankey',
  standalone: true,
  imports: [MatIconModule, MdsIconsModule, DividerComponent],
  templateUrl: './sankey.component.html',
  styleUrls: ['./sankey.component.scss']
})
export class SankeyComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() data: any;
  ngxEventBus: Subscription[] = [];
  orgJSON: any;
  graph: any;
  icons = MdsIcon;
  private windowData: any = window;
  private transformMatrix: TransformMatrix;
  private destroy$ = new Subject<void>();
  dividerSpacing = MdsSizeType.SMALL;
  @ViewChild('customMenuList') customMenuList!: ElementRef;
  menuList: ElementRef<any>;
  private previousClickedRid: string = null;
  private menuService = inject(MenuService);

  private destroyRef = inject(DestroyRef);
  constructor(
    public eventDelegatorService: EventDelegatorService,
    private sankeyOthersService: SankeyOthersService,
    public dialog: MatDialog,
    public reusableService: ReusableService,
    private makersiteService: MakersiteService,
    private toastrService: ToastrService,
    private analyzeFiltersService: AnalyzeFiltersService,
    private globalDataService: GlobalDataService,
    private router: Router,
    private analyzeService: AnalyzeService
  ) {}

  @ViewChild('sankey') sankey: ElementRef;
  currentClickNode: any;
  legend_data = [];
  temp = []; //    legend_obj = {name ,nltype : any; color:any;type:any};
  lObj = {};
  sidepanelData: any;
  menuDataSet = [];
  showEditOriginal = false;
  rectY: any;
  rectX: any;
  ori: any;
  allData: any;
  filteredData: any;
  private legend: SankyLegend;
  private legendLeftBottomMargin = 16;
  public showAllAssociatedData;
  public activeLayer: any;
  private storedElement!: HTMLElement;
  ngOnInit() {
    this.showAllAssociatedData = !this.globalDataService.getAnalysisFilter();
    this.ori = _.cloneDeep(this.data.data);

    this.reusableService.productValidationChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
      if (!val) return;
      this.updateValidationPill(val);
    });
    this.ngxEventBus.push(
      this.globalDataService.getActiveApp().subscribe((data: any) => {
        this.activeLayer = data;
      })
    );

    this.ngxEventBus.push(
      this.eventDelegatorService.on('ngx-event-analyze', (ev) => {
        if (ev.action === 'save-sankey-action') {
          this.saveSankey(ev.data);
        } else if (ev.action === 'toggle-all-switch' || ev.action === 'back-to-default') {
          this.showAllAssociatedData = !this.globalDataService.getAnalysisFilter();
          this.reDrawSankey();
        } else if (ev.action === 'svg-resize') {
          this.stretchSankey();
        }
      })
    );
  }

  ngAfterViewInit(): void {
    this.init();
    this.storedElement = this.customMenuList.nativeElement;
    const resizeSubscription = fromEvent(window, 'resize')
      .pipe(debounceTime(100))
      .subscribe(() => this.stretchSankey());
    this.ngxEventBus.push(resizeSubscription);
  }

  ngOnDestroy() {
    if (this.sankey && this.sankey.nativeElement?.children.length > 0) {
      this.sankey.nativeElement.children[0]?.remove();
    }
    this.ngxEventBus.forEach((e) => e?.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
  }

  init() {
    this.allData = this.sankeyOthersService.filterData(this.data.data);
    this.filteredData = this.analyzeFiltersService.filterData(this.data.data, this.activeLayer);
    this.reDrawSankey();
  }

  reDrawSankey(data?: any) {
    if (this.sankey && this.sankey.nativeElement?.children.length > 0) {
      this.sankey.nativeElement.children[0]?.remove();
    }

    if (data) {
      this.drawChart(_.cloneDeep(data));
    } else if (this.showAllAssociatedData || this.activeLayer.category === 'Environment') {
      if (this.activeLayer.layer === 'costing') {
        // nodes parent array contains id of it's own and it's target ids upto 0 level node.  here nodes are filtered upto level 3 therefore we filtered nodes of sankey based on length of parents length which will be 6 in case of level 3
        const parentsLength = 6;
        this.filteredData.nodes = this.filteredData.nodes.filter((obj) => obj.parents.length <= parentsLength);
        // store filtered nodes ids to check with links source and target value
        const nodesId = this.filteredData.nodes.map((obj) => obj.id);
        // filter links with source and target value which are available in nodes id
        this.filteredData.links = this.filteredData.links.filter(
          (obj) => nodesId.includes(obj.target) && nodesId.includes(obj.source)
        );
      }
      this.drawChart(_.cloneDeep(this.filteredData));
    } else {
      this.drawChart(_.cloneDeep(this.allData));
    }
  }

  clearTooltip() {
    document.querySelectorAll('.tooltip').forEach((element: any) => {
      element.style.opacity = 0;
    });
  }

  onlyLiveJsonLinks = [];
  onlyLiveJsonNodes = [];
  expandedLiveJsonNodes = [];
  expandedLiveJsonLinks = [];
  node: any;

  chartData: any;

  drawChart(chartData) {
    this.chartData = _.cloneDeep(chartData);
    if (chartData.nodes && chartData.nodes.length === 0) {
      return;
    }
    // if grouping is enabled, filter out hidden nodes with low contribution
    const groupNodes = this.sankeyOthersService.isEnabled;
    this.globalDataService.setHasGroupedNodes(this.sankeyOthersService.hasGroupedNodes);
    if (groupNodes) {
      const hiddenNodesList = chartData.nodes.filter((n) => n.hidden).map((n) => n.id);
      chartData.links = chartData.links.filter(
        (l) => !hiddenNodesList.includes(l.source) && !hiddenNodesList.includes(l.target)
      );
      chartData.nodes = chartData.nodes
        .filter((n) => !n.hidden)
        .map((n) => {
          n.parents = n.parents.filter((p) => !hiddenNodesList.includes(p));
          return n;
        });
    }

    const self = this;
    self.currentClickNode = undefined;
    self.legend_data = [];

    this.setSankeyHeaders(chartData);
    setMenuDataset();

    function tripleDot(d) {
      const self = d3.select(this);
      const clipWidth = +d3.select(this.previousSibling).select('rect').attr('width');
      const positionX = +d3.select(this.previousSibling).select('rect').attr('x');
      const textWidth = self.node().getComputedTextLength();
      if (textWidth > clipWidth) {
        d3.select(this.nextSibling)
          .style('visibility', 'visible')
          .attr('x', (d: any) => positionX + clipWidth);
      } else {
        d3.select(this.nextSibling).style('visibility', 'hidden');
      }
    }

    const legenddata = this.legend_data;
    const { width, height } = this.analyzeService.getViewportSize('sankey');
    const svg = this.createSVG(width, height);
    d3.select(this.sankey.nativeElement).call(d3.zoom().on('zoom', zoomFun)).on('dblclick.zoom', null);
    const child = this.createChildGroup(svg, width, height);

    const sankeyHeight = Math.max(chartData.nodes?.length * 50, height);
    const sankeyWidth = width * 0.96;
    const sankeyNodePadding = 30;
    let highestEdgeCount = 1;
    chartData.nodes.forEach((node) => {
      highestEdgeCount = Math.max(highestEdgeCount, node.edge_path?.length ?? 1);
    });
    const nodeWidth = 250;
    const sankey = d3Sankey
      .sankey()
      .nodeId((d: any) => d.id)
      .nodeWidth(5.66)
      .nodePadding(sankeyNodePadding)
      .nodeAlign((node, n) => d3Sankey.sankeyRight(node, n))
      .extent([
        [0, 0],
        [Math.max(nodeWidth * (highestEdgeCount + 1), sankeyWidth), sankeyHeight]
      ]);

    let { link, node } = this.createLinksAndNodes(child, sankey);
    this.graph = getLayoutGraph();
    sankey(this.graph);

    // NOTE: we create a custom algorithm to sort and reposition nodes to prevent connector overlap, recursively moving left to right and back again over each level.
    // It seems that just modifying .iterations() on the sankey either doesn't work, or isn't enough. Possibly due to our local fork of d3 sankey.
    // We should in the future look to move back to using it from npm package and see if it resolves some of these issues.
    // This solution was achieved largely using AI and iterating over the results until the sankeys were displaying correctly.
    this.minimizeCrossings(this.graph, 4, sankeyNodePadding);
    sankey.update(this.graph);

    const colorList = [
      ColorAliases.SANKEY_LEVEL_1,
      ColorAliases.SANKEY_LEVEL_2,
      ColorAliases.SANKEY_LEVEL_3,
      ColorAliases.SANKEY_LEVEL_4,
      ColorAliases.SANKEY_LEVEL_5,
      ColorAliases.SANKEY_LEVEL_6,
      ColorAliases.SANKEY_LEVEL_7,
      ColorAliases.SANKEY_LEVEL_8,
      ColorAliases.SANKEY_LEVEL_9,
      ColorAliases.SANKEY_LEVEL_10,
      ColorAliases.SANKEY_LEVEL_11,
      ColorAliases.SANKEY_LEVEL_12
    ];
    const unitArray = [];
    let sidepanelData = {};
    this.graph.links.forEach((d, i) => {
      if (unitArray.indexOf(d.unit) === -1 && d.unit !== '') {
        unitArray.push(d.unit);
      }
    });
    // Create <defs> for gradients and filters
    const defs = svg.append('defs');

    // Gradient
    const gradient2 = defs
      .append('linearGradient')
      .attr('id', 'gradient-accent-secondary')
      .attr('x1', '100%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '0%');

    gradient2.append('stop').attr('offset', '-0.22%').attr('stop-color', 'rgba(255, 167, 107, 0.15)');

    gradient2.append('stop').attr('offset', '49.89%').attr('stop-color', 'rgba(200, 224, 0, 0.15)');

    gradient2.append('stop').attr('offset', '100%').attr('stop-color', 'rgba(51, 144, 149, 0.15)');

    link = link
      .data(this.graph.links)
      .enter()
      .append('path')
      .attr('d', sankeyLinkPath)
      .attr('stroke-opacity', 0.4)
      .attr('stroke-width', '1')
      .style('fill', (d: any) => {
        if (false && this.sankeyOthersService.isDebug && d.other) {
          // disabling the debug
          return ColorAliases.BACKGROUND_THEME_PRIMARY;
        }
        return `${getColor.call(this, d)}32`;
      })
      .on('mouseover', (event, d: any) => {
        d3.select(event.currentTarget).style('fill', 'url(#gradient-accent-secondary)').attr('opacity', 1);

        // Show tooltip
        tooltip.style('opacity', 1);
      })

      .on('mousemove', (event, d: any) => {
        const { pageX } = event;
        const { pageY } = event;

        if (tooltipTimeout) {
          self.clearTooltip();
          clearTimeout(tooltipTimeout);
        }
        tooltipTimeout = setTimeout(() => {
          tooltip.style('opacity', 1);
          tooltip.style('position', 'absolute');
          tooltip.style('visibility', 'visible');
          tooltip.style('z-index', 100);
          let nodeValue = d.disp_value ? d.disp_value : d.value;
          nodeValue = self.reusableService.numberFormate(nodeValue);
          const titleLen = `${`${d.source.name} → ${d.target.name}${nodeValue}`.length * 8}px`;
          let unit = d.unit ? d.unit : '';
          if (['recycled_content', 'eol_rir'].includes(self.activeLayer?.layer)) {
            nodeValue = d.recycled_content === 0 || d.eol_rir === 0 ? 0 : d.recycled_content || d.eol_rir;
            if (nodeValue && nodeValue.toString().split('.')[1] && nodeValue.toString().split('.')[1].length > 2) {
              nodeValue = nodeValue * 100 < 10 ? nodeValue.toExponential(2) : nodeValue.toFixed(2);
            }
            unit = '%';
          } else if (['supply_risk'].includes(self.activeLayer?.layer)) {
            nodeValue = d.risk_score;
            unit = d.risk_indicator;
          }
          const text = d.risk_indicator === 'Unknown' ? '&nbsp' : `<span>&nbsp${nodeValue}</span>`;

          tooltip.html(
            `<div style='min-width:200px;max-width:450px;height: 143.45px;color:#00121C; background-color:#FCFCFD;width:${titleLen};box-shadow: 1 px 2px 4px -3px rgba(66, 69, 75, 0.15); border: 1.06px #003C4F solid !important;padding: 16.98px'>` +
              `<div style='color: #00121C; font-size: 22.64px; font-family: Roboto; font-weight: 700; word-wrap: break-word;display:flex'>${text}&nbsp<div style="color: #335568; font-size: 12.74px; font-family: Roboto; font-weight: 400; word-wrap: break-word">${unit}</div></div>` +
              '<div style="color: #547080; font-size: 12.74px; font-family: Roboto; font-weight: 400; word-wrap: break-word;padding-top:4.25px">From</div>' +
              `<label style="color: #00121C; font-size: 14.86px; font-family: Roboto; font-weight: 400; line-height: 21.23px; word-wrap: break-word">${d.source.name}</label>` +
              '<div style="color: #547080; font-size: 12.74px; font-family: Roboto; font-weight: 400; word-wrap: break-word;padding-top:4.25px">To</div>' +
              `<label style="color: #00121C; font-size: 14.86px; font-family: Roboto; font-weight: 400; line-height: 21.23px; word-wrap: break-word">${d.target.name}</label>` +
              '</div>' +
              '</div>'
          );
          tooltip.style('left', `${pageX + 15}px`);
          tooltip.style('top', `${pageY - 20}px`);
          tooltip.style('pointer-events', 'none');
        }, 200);
        return true;
      })
      .on('mouseout', function (event, d) {
        // Use `function`, not arrow function
        d3.select(this) // `this` now correctly refers to the hovered element
          .style('fill', (d: any) => {
            // Use function for correct context
            if (false && self.sankeyOthersService.isDebug && d.other) {
              return ColorAliases.BACKGROUND_THEME_PRIMARY;
            }
            return `${getColor.call(self, d)}32`; // Reset to original color
          })
          .attr('stroke-opacity', 0.4);

        self.clearTooltip(); // Angular component method

        if (tooltipTimeout) {
          clearTimeout(tooltipTimeout);
        }
      })
      .on('click', (d, event: any) => {
        let nodeValue;
        if (event.disp_value !== undefined) {
          nodeValue = event.disp_value;
        } else {
          nodeValue = event.value;
        }
        let unit = '';
        if (event.unit) {
          unit = event.unit;
        } else {
          unit = self.activeLayer === undefined ? '' : self.activeLayer.unit;
        }
        let nodeAmount;
        if (event.disp_value !== undefined) {
          nodeAmount = event.disp_value;
        } else {
          nodeAmount = event.value;
        }
        if (['recycled_content', 'eol_rir'].includes(self.activeLayer?.layer)) {
          nodeAmount = event.recycled_content || event.eol_rir;
          unit = '%';
        }
        const sankeyEgdeData = {
          value: nodeAmount,
          unit,
          sourceNode: event.source.name,
          targetNode: event.target.name,
          type_label: event.source.type_label
        };

        const data: Record<string, any> = {};
        data['node-info'] = sankeyEgdeData;
        data.isFrom = 'Sankey-Egde-Sidenav';
        data.module = 'mattermaps';
        data.layer = self.activeLayer;
        sidepanelData = data;
        // self.sidepanelData =sidepanelData
        self.eventDelegatorService.emiter('custom-ngx-event', {
          type: 'sankey',
          action: 'onclickLink',
          data: sidepanelData,
          controller: window.location.pathname
        });
      });
    let tooltipTimeout;
    d3.selectAll('.tooltip').remove();
    let tooltip = d3.select('body').append('div').attr('class', 'tooltip').style('opacity', 0);
    if (this.graph.nodes.some((row) => row.bom_line)) {
      this.reusableService.pradicateBy(this.graph.nodes, 'bom_line');
    }
    node = node
      .data(this.graph.nodes)
      .enter()
      .append('g')
      .attr('transform', (d, event: any) => {
        if (!isNaN(event.x) && this.graph.links.length > 0) {
          return `translate(${event.x},${event.y})`;
        }
      })
      .on('contextmenu', (d, event) => {
        const windowHeight = this.sankey.nativeElement.clientHeight;
        menuPosition(100, d, 'visible', event, this.storedElement, windowHeight);
        openwheel(d, this, d3.pointer(event), event, this.storedElement);
      })
      .call(
        d3
          .drag()
          .subject((d) => {
            if (d.sourceEvent.target.tagName === 'rect') return d;
          })
          .on('drag', dragmove)
      )

      .on('click', (event: any, data) => {
        self.currentClickNode = data;
        let touchtime = 0;
        if (self.data.isMobile) {
          if (new Date().getTime() - touchtime < 500) {
            if (event.defaultPrevented) {
              return;
            }
            let opacity;
            self.onlyLiveJsonLinks = [];
            self.onlyLiveJsonNodes = [];
            self.expandedLiveJsonNodes = [];
            self.expandedLiveJsonLinks = [];
            if (event.visible || event.visible === undefined) {
              offNodeVisibility(event);
              if (self.onlyLiveJsonNodes.length > 0) {
                opacity = 0;
                event.visible = false;
                renderCollapsedLiveDomSankey(event);
              }
            } else {
              opacity = 1;
              event.visible = true;
              onExpandNodeVisibility(event);
              renderExpandLiveDomSankey(event);
            }

            touchtime = 0;
          } else {
            touchtime = new Date().getTime();
          }
        }
        if (this.previousClickedRid === undefined || this.previousClickedRid !== data.rid) {
          this.previousClickedRid = data.rid?.replace('#', '');

          highlightNodeLinksPrestep(data, event);
        }
      })
      .on('dblclick', (d, event: any) => {
        self.reDrawSankey(self.ori);
        // on double click reset dom with leaves folding
        if (!self.data.isMobile) {
          let opacity;
          self.onlyLiveJsonLinks = [];
          self.onlyLiveJsonNodes = [];
          self.expandedLiveJsonNodes = [];
          self.expandedLiveJsonLinks = [];
          if (event.visible || event.visible === undefined) {
            filterselection();
            offNodeVisibility(event);
            if (self.onlyLiveJsonNodes.length > 0) {
              opacity = 0;
              event.visible = false;
              renderCollapsedLiveDomSankey(event);
            }
          } else {
            opacity = 1;
            event.visible = true;
            onExpandNodeVisibility(event);
            renderExpandLiveDomSankey(event);
          }
        }
      });
    node
      .append('rect')
      .attr('x', (d: any) => {
        if (isNaN(d.x0)) d.x0 = 0;
        if (isNaN(d.x1)) d.x1 = d.x0 + 12;
        return d.x0;
      })
      .attr('y', (d: any) => {
        if (isNaN(d.y0)) d.y0 = 0;
        if (isNaN(d.y1)) d.y1 = d.y0 + 3;
        return d.y0;
      })
      .attr('height', (d: any) => d.y1 - d.y0)
      .attr('width', '14')
      .attr('fill', (event, d) => (d = nodeColor(event, 1)))
      .attr('fill-opacity', (d: any) => (self.isAiNodeAndValidationRequired(d) ? 0.5 : 1))
      .style('cursor', 'all-scroll')
      .attr('stroke', 'rgba(0, 0, 0, 0.50)') // Set the border color
      .attr('stroke-width', (d: any) => (self.isAiNodeAndValidationRequired(d) ? 1.5 : 0.71))
      .attr('stroke-dasharray', (d: any) => (self.isAiNodeAndValidationRequired(d) ? '5,5' : null));

    const pinicon = node
      .append('image')
      .attr('href', (d: any) => {
        if (d.node_pins && d.node_pins.length > 0) {
          return '/assets/icons/composer-data-objects/sankey-pin.svg';
        }
      })
      .style('cursor', (d: any) => {
        if (d?.node_pins?.length > 0) {
          return 'pointer';
        }
        return 'default';
      })
      .on('click', () => {
        this.menuService.setSelectedAction({
          label: 'Pins',
          key: 'pins',
          icon: MdsIcon.PIN,
          selected: false,
          disabled: false,
          menuTrigger: '',
          visible: true
        });
      })
      .attr('x', (d: any) => d.x0 - 50)
      .attr('y', (d: any) => (d.y1 + d.y0) / 2 - 12)
      .attr('width', 58)
      .attr('height', (d: any) => {
        if (d.node_pins && d.node_pins.length > 0) {
          return 32;
        }
      });
    const pinTooltip = d3.select('body').append('div').attr('class', 'tooltip').style('opacity', 0);

    let pinTooltipTimeout;
    pinicon
      .on('mouseover', (d, event) => {
        if (d.node_pins && d.node_pins.length > 0) {
          const { pageX } = d;
          const { pageY } = d;

          if (pinTooltipTimeout) {
            pinTooltip.style('opacity', 0);
            clearTimeout(pinTooltipTimeout);
          }

          pinTooltipTimeout = setTimeout(() => {
            pinTooltip.style('opacity', 1);
            pinTooltip.style('position', 'absolute');
            pinTooltip.style('visibility', 'visible');
            pinTooltip.style('z-index', 100);

            const pinTooltipDiv = document.createElement('div');
            let str = '';
            for (const i in d.node_pins) {
              str += `<div flex='100' layout='column' layout-align='space-around center' style='padding-left: 5px; padding-right:5px;'>  <div style='font-size:14px;padding-right:6px;'>${
                d.node_pins[i].label
              }</div> <div style='font-size:14px;margin: 6px 0px;'>${d.node_pins[i].value}</div> </div>`;
            }
            if (d.node_pins && d.node_pins.length > 0) {
              pinTooltipDiv.innerHTML =
                "<div style='min-width:200px;max-width:450px;color:#FFFFFF; background-color:#4c4c4c;border-radius:4px;min-height:50px;width:" +
                `'>${str}</div>`;
            }
            pinTooltip.html(pinTooltipDiv.innerHTML);

            pinTooltip.style('left', `${pageX + 15}px`);
            pinTooltip.style('top', `${pageY - 20}px`);
            pinTooltip.style('pointer-events', 'none');
          }, 200);
          return true;
        }
      })
      .on('mouseout', (d) => {
        pinTooltip.style('opacity', 0);
        if (pinTooltipTimeout) {
          clearTimeout(pinTooltipTimeout);
        }
      });

    node
      .append('clipPath')
      .attr('id', (d: any) => `clipTxt${d.id}`)
      .append('rect')
      .attr('x', (d: any) => d.x0 + 12) // start a bit inside the node
      .attr('y', (d: any) => d.y0 - 20) // go above the node to avoid top clipping
      .attr('width', (d: any) => {
        const target = d.sourceLinks?.[0]?.target;
        if (target) return target?.x1 - d.x1 - (24 + 50);
        return 600;
      })
      .attr('height', (d: any) => d.y1 - d.y0 + 30); // tall enough to cover text + padding

    node
      .append('text')
      .attr('x', (d: any) => d.x0 + 24)
      .attr('y', (d: any) => (d.y1 + d.y0) / 2)
      .attr('dy', (d: any) => (self.isAiNodeAndValidationRequired(d) ? '-0.2em' : '0.35em')) // shift up if validation
      .attr('width', (d: any) => {
        const target = d.sourceLinks?.[0]?.target;
        if (target) return target?.x1 - d.x1 - (24 + 20);
        return 600;
      })
      .text((d: any) => {
        if (groupNodes && d.others?.length > 0 && !this.sankeyOthersService.isExpanded(d.id)) {
          return `[${d.others.length} others] ${d.name}`;
        }
        return d.name;
      })
      .style('font-weight', (d: any) => (groupNodes && d.others?.length > 0 ? 'bold' : 'normal'))
      .style('font-size', '1rem')
      .style('letter-spacing', 'normal')
      .style('cursor', 'default')
      .attr('clip-path', (d: any) => `url(#clipTxt${d.id})`) // apply clip horizontally
      .filter((d: any) => chartData.nodes?.length > 1 && d.parents.length > 1)
      .attr('text-anchor', 'start')
      .attr('class', 'text-label');

    node
      .append('text')
      .attr('x', (d: any) => d.x0 + 24)
      .attr('y', (d: any) => (d.y1 + d.y0) / 2)
      .attr('dy', (d: any) => (self.isAiNodeAndValidationRequired(d) ? '-0.2em' : '0.35em'))
      .attr('width', 12)
      .text('...')
      .style('font-size', '1rem')
      .style('visibility', 'hidden')
      .attr('class', 'triple-dot');
    node.select('text').each(tripleDot);

    self.addValidationPill(node, AIValidationStatuses.PENDING);
    self.addValidationPill(node, AIValidationStatuses.REJECTED);
    // self.addValidationPill(svg);
    node
      .append('image')
      .attr('x', (d: any) => {
        const baseX = d.x0;
        const offset = d.node_pins && d.node_pins.length > 0 ? -80 : -40;
        return baseX + offset;
      })
      .attr('y', (d: any) => (d.y1 + d.y0) / 2 - 12) // Adjust Y position to align with text
      .attr('width', 32) // Adjust as needed
      .attr('height', 32) // Adjust as needed
      .attr('xlink:href', (d: any) => {
        if (d.has_alternatives) {
          return 'assets/icons/composer-data-objects/alternative_available_legend.svg';
        }
        if (d.node_type === 'commodity') {
          return 'assets/icons/composer-data-objects/Commodity_legend.svg';
        }
        if (['alternative', 'Alternative Applied'].includes(d.legend)) {
          return 'assets/icons/composer-data-objects/alternative_applied_legend.svg';
        }
      })
      .style('cursor', (d: any) => {
        if (d.has_alternatives || ['alternative', 'Alternative Applied', 'commodity'].includes(d.legend)) {
          return 'pointer';
        }
        return 'default';
      })
      .on('click', (event: any, d: any) => {
        if (d.has_alternatives || ['alternative', 'Alternative Applied', 'commodity'].includes(d.legend)) {
          event.stopPropagation();
          const windowHeight = self.sankey.nativeElement.clientHeight;
          menuPosition(100, event, 'visible', d, this.storedElement, windowHeight);
          openwheel(event, null, d3.pointer(event), d, this.storedElement);
        }
      });

    this.legend = new SankyLegend(svg, legenddata);
    this.legend.setPosition(
      this.legendLeftBottomMargin,
      height - this.legendLeftBottomMargin,
      this.sankey.nativeElement.clientHeight + 32
    );

    const linkedByIndex = {};
    this.graph.links.forEach((d) => {
      if (linkedByIndex[`${d.source.id},${d.target.id}`] === 1) {
        // if bidirectional links
        // if bidirectional links
        // if bidirectional links
      } else {
        linkedByIndex[`${d.source.id},${d.target.id}`] = 1;
      }
    });

    const linkedByIndexOrgDom = {};
    if (!this.data.isready) {
      this.orgJSON = _.cloneDeep(this.graph);
      const orgJsonLinks = _.cloneDeep(this.graph.links);
      orgJsonLinks.forEach((d) => {
        if (linkedByIndexOrgDom[`${d.source.id},${d.target.id}`] === 1) {
          // if bidirectional links
          // if bidirectional links
          // if bidirectional links
        } else {
          linkedByIndexOrgDom[`${d.source.id},${d.target.id}`] = 1;
        }
      });
    }

    function sankeyLinkPath(link) {
      // this is a drop in replacement for d3.sankeyLinkHorizontal()
      // well, without the accessors/options
      const sx = link.source.x1;
      const tx = link.target.x0 + 1;
      const sy0 = link.y0 - link.width / 2;
      const sy1 = link.y0 + link.width / 2;
      const ty0 = link.y1 - link.width / 2;
      const ty1 = link.y1 + link.width / 2;

      const halfx = (tx - sx) / 2;

      const path = d3.path();
      path.moveTo(sx, sy0);

      let cpx1 = sx + halfx;
      let cpy1 = sy0;
      let cpx2 = sx + halfx;
      let cpy2 = ty0;
      path.bezierCurveTo(cpx1, cpy1, cpx2, cpy2, tx, ty0);
      path.lineTo(tx, ty1);

      cpx1 = sx + halfx;
      cpy1 = ty1;
      cpx2 = sx + halfx;
      cpy2 = sy1;
      path.bezierCurveTo(cpx1, cpy1, cpx2, cpy2, sx, sy1);
      path.lineTo(sx, sy0);
      return path.toString();
    }

    /**
     * @param event - d3 zoom in and zoom out event
     * limits the zoom in / zoom out to 2.5x and 0.5x
     * mouse scroll also moves the position of the graph, resets the position coordinates to previous position
     */

    function zoomFun(event) {
      const translate = [];
      if (event.transform.k < 0.5 || event.transform.k > 2.5) {
        event.transform.k = event.transform.k > 2.5 ? 2.5 : 0.5;
        event.transform.x = self.transformMatrix.x;
        event.transform.y = self.transformMatrix.y;
        child.attr('transform', event.transform);
        if (event.transform.k !== 0.8 || event.transform.k !== 1) {
          self.eventDelegatorService.emiter('custom-ngx-event', {
            type: 'sankey',
            action: 'zoom',
            controller: window.location.pathname
          });
        }
        return;
      }

      if (!node) return;

      self.transformMatrix = event.transform;
      const scaledFontSize = (event.transform.k.toFixed(1) * 2 + 10) / event.transform.k;
      node.selectAll('.text-label').style('font-size', `${Math.min(16, scaledFontSize)}px`);
      child.attr('transform', event.transform);

      node.select('text').each(tripleDot);

      if (event.transform.k > 0.49) {
        translate[0] = event.transform.x;
        translate[1] = event.transform.y + 60;
        child.attr('transform', `translate(${translate})` + ` scale(${event.transform.k})`);
      }

      if (event.transform.k !== 0.8 || event.transform.k !== 1) {
        self.eventDelegatorService.emiter('custom-ngx-event', {
          type: 'sankey',
          action: 'zoom',
          controller: window.location.pathname
        });
      }
    }

    function menuPosition(zIndex, ev, visibility, node, menulist, windowHeight) {
      self.showEditOriginal = false;
      if (node.rid && node.rid_original && node.rid.replace('#', '') !== node.rid_original.replace('#', '')) {
        self.showEditOriginal = true;
      }
      if (ev.clientY > windowHeight - 150) {
        menulist.style.top = `${ev.pageY - 330}px`;
      } else {
        menulist.style.top = `${ev.pageY - 115}px`;
      }
      menulist.style.visibility = visibility;
      menulist.style.zIndex = zIndex;
      menulist.style.left = `${ev.pageX - 20}px`;
      if (ev.preventDefault) {
        ev.preventDefault();
      }
    }

    function openwheel(d, _this, position, event, menulist) {
      const isClicked = false;
      let positionY;
      if (position[1]) {
        positionY = position[1];
      } else {
        positionY = d.dy;
      }
      self.currentClickNode = event;
      // For showing Edit original rid for sankey only

      if (
        (event.type === 'product' || event.type === 'process' || event.type === 'company') &&
        !['recycled_content', 'eol_rir'].includes(self.activeLayer.layer)
      ) {
        self.menuDataSet.push('substitute');
      }

      if (event.type === 'product') {
        self.menuDataSet.push('alternative');
      }

      d3.select('body').on('click', () => {
        menuPosition(-1, 0, 'hidden', event, menulist, '');
      });
    }

    function dragmove(ev, node) {
      self.rectY = d3.select(this).select('rect').attr('y');
      self.rectX = d3.select(this).select('rect').attr('x');
      node.y0 += ev.dy;
      node.x0 += ev.dx;
      node.x1 += ev.dx;
      node.y1 += ev.dy;

      const target = node.sourceLinks?.[0]?.target;
      const source = (node.sourceLinks?.[0]?.source.y0 + node.sourceLinks?.[0]?.source.y1) / 2;
      if (source && target) {
        let distance = target?.x1 - node.x1 - (24 + 22);
        distance = distance > 120 ? distance : 600;
        if (source < target.y0 - 15 || source > target.y1 + 15) {
          distance = 600;
        }
        const nodeElement = d3.select(`#clipTxt${node.id} rect`);
        nodeElement.attr('width', distance);
        d3.select(this).select('text').attr('width', distance).each(tripleDot);
      }

      const yTranslate = node.y0 - self.rectY;
      const xTranslate = node.x0 - self.rectX;

      d3.select(this).attr('transform', `translate(${xTranslate},${yTranslate})`);

      sankey.update(self.graph);
      link.attr('d', sankeyLinkPath);
    }

    function setMenuDataset() {
      const featureArray = self.globalDataService.getFeatureArray();
      if (featureArray.indexOf('mattermaps') !== -1) {
        self.menuDataSet.push('info');
        self.menuDataSet.push('navigate');
        self.menuDataSet.push('savepng');
      }

      if (featureArray.indexOf('composer_process') !== -1) {
        self.menuDataSet.push('edit');
      }

      if (featureArray.indexOf('todos') !== -1) {
        self.menuDataSet.push('todo');
      }
    }

    function getLayoutGraph() {
      return {
        ...chartData,
        nodes: chartData.nodes.map((chartNode) => ({ ...chartNode })),
        links: chartData.links.map((chartLink) => {
          const rawValue = chartLink.value ?? chartLink.amount ?? 0;

          return {
            ...chartLink,
            value: rawValue,
            layoutValue: Math.abs(rawValue),
            disp_value: chartLink.disp_value ?? rawValue
          };
        })
      };
    }

    /**
     *
     * @param percentage - this is percentage value (0 to 100)
     * @returns color based on percentage - rage between min and max color
     */

    function colorGenerator(value, gradientData) {
      if (value == null) return gradientData.default_color;
      const percentage = Math.ceil(value);
      gradientData.max_color = '#01c9b3';
      gradientData.min_color = '#ff5f00';
      const colorStops = [
        { percentage: 10, color: gradientData.min_color },
        { percentage: 20, color: '#ff8f01' },
        { percentage: 30, color: '#ffb101' },
        { percentage: 40, color: '#ffd101' },
        { percentage: 50, color: '#fff101' },
        { percentage: 60, color: '#e3fa15' },
        { percentage: 70, color: '#b1ef38' },
        { percentage: 80, color: '#7de45d' },
        { percentage: 90, color: '#49d981' },
        { percentage: 100, color: gradientData.max_color }
      ];
      if (!percentage) return gradientData.min_color;
      const calculatedPercentage = Math.ceil(percentage / 10) * 10;
      const selectedPercentageColor = colorStops.find((item) => item.percentage === calculatedPercentage);
      if (!selectedPercentageColor && calculatedPercentage) {
        if (calculatedPercentage > 100) {
          return gradientData.max_color;
        }
        return gradientData.min_color;
      }
      return selectedPercentageColor.color;
    }

    function getColor(d) {
      if (
        d.disp_value < 0 &&
        !['supply_risk', 'costing', 'recycled_content', 'eol_rir'].includes(self.activeLayer.layer)
      ) {
        return ColorAliases.SANKEY_NEGATIVE_VALUE;
      }
      if (unitArray.indexOf(d.unit) !== -1) {
        if (['recycled_content'].includes(self.activeLayer?.layer)) {
          const gradientData = self.legend_data.find((e) => e.type === 'gradient');
          return colorGenerator(d.recycled_content, gradientData);
        }
        if (['eol_rir'].includes(self.activeLayer?.layer)) {
          const gradientData = self.legend_data.find((e) => e.type === 'gradient');
          return colorGenerator(d.eol_rir, gradientData);
        }
        if (['supply_risk'].includes(self.activeLayer?.layer)) {
          const legend = this.graph.risk_legend.find((e) => {
            if (e.type === d.risk_score) return e;
          });
          return legend && legend.color;
        }
        if (window.location.pathname.includes('costing')) {
          if (d.legend === 'marketprice') return this.graph.edge_legend[0].color;
          if (d.legend === 'shouldcost') return this.graph.edge_legend[1].color;
        } else if (self.activeLayer?.layer === 'recycled_content') {
          const legend = this.graph.edge_legend.find((e) => {
            if (e.type === d.impact) return e;
          });
          return legend?.color || ColorAliases.BACKGROUND_THEME_PRIMARY;
        } else if (d.unit === 'kg' || d.unit === 'Kg') {
          return ColorAliases.SANKEY_LEVEL_1;
        } else if (d.unit === 'unit' || d.unit === 'Unit') {
          return ColorAliases.BACKGROUND_THEME_SECONDARY;
        } else if (d.unit === 'mj' || d.unit === 'MJ') {
          return ColorAliases.BACKGROUND_THEME_TERTIARY;
        } else {
          return colorList[unitArray.indexOf(d.unit)];
        }
      } else {
        return ColorAliases.NODE_PRIMARY;
      }
    }

    function nodeColor(node, depth) {
      const colors = {
        process: ColorAliases.NODE_SECONDARY,
        Process: ColorAliases.NODE_SECONDARY,
        product: ColorAliases.SECONDARY,
        'Product Group': ColorAliases.SECONDARY,
        alternative: ColorAliases.NODE_STRONG,
        'Alternative Applied': ColorAliases.NODE_STRONG,
        has_alternatives: ColorAliases.NODE_STRONG,
        'Alternative Available': ColorAliases.NODE_STRONG,
        modified: ColorAliases.NODE_TERTIARY,
        sector: ColorAliases.ON_WARNING,
        company: ColorAliases.SANKEY_LEVEL_13,
        Company: ColorAliases.SANKEY_LEVEL_13
      };
      const type = node.type ? node.type : node.type_label;
      if (type) {
        if (node.modified) {
          return ColorAliases.NODE_TERTIARY;
        }
        if (node.has_alternatives === true) {
          for (const i in legenddata) {
            if (legenddata[i].nltype === 'has_alternatives' || node.legend === legenddata[i].nltype) {
              return legenddata[i].color ? legenddata[i].color : colors[type];
            }
          }
        } else if (
          node.rid &&
          node.rid_original &&
          node.rid.replace('#', '') !== node.rid_original.replace('#', '') &&
          node.node_type !== 'commodity'
        ) {
          return ColorAliases.SANKEY_LEVEL_10;
        } else if (colors[node.legend]) {
          for (const i in legenddata) {
            if (node.type === legenddata[i].nltype) {
              return legenddata[i].color ? legenddata[i].color : colors[type];
            }
          }
        } else if (colors[type]) {
          return colors[type];
        } else {
          return null;
        }
      }
      return null;
    }

    function isConnectedLink(a, b, c) {
      if (c) {
        return linkedByIndexOrgDom[`${b.id},${a.id}`] || a.id === b.id;
      }
      return linkedByIndex[`${b.id},${a.id}`] || a.id === b.id;
    }
    const node1 = [];

    function filterselection() {
      node.filter((o) => {
        node1.push(o);
        return true;
      });
    }

    function offNodeVisibility(d) {
      node1.filter((o) => {
        if (isConnectedLink(d, o, undefined) === 1) {
          offNodeVisibility(o);
          self.onlyLiveJsonNodes.push(o.id);
          return isConnectedLink(d, o, undefined);
        }
        return false;
      });
      link.filter((o: any) => {
        if (isConnectedLink(d, o.source, false) === 1) {
          self.onlyLiveJsonLinks.push(o.source.id);
          return isConnectedLink(d, o.source, false);
        }
        return false;
      });
    }

    function onExpandNodeVisibility(d) {
      self.orgJSON.nodes.filter((o) => {
        if (isConnectedLink(d, o, true) === 1) {
          onExpandNodeVisibility(o);
          self.expandedLiveJsonNodes.push(o.id);
          return isConnectedLink(d, o, true);
        }
        return false;
      });

      self.orgJSON.links.filter((o) => {
        if (isConnectedLink(d, o.source, true) === 1) {
          self.expandedLiveJsonLinks.push(o.source.id);
          return isConnectedLink(d, o.source, true);
        }
        return false;
      });
    }

    function renderCollapsedLiveDomSankey(activeNode: any) {
      const livejson = _.cloneDeep(chartData);
      const nodearray = [];
      const idleNodes = [];
      livejson.nodes.forEach((d) => {
        if (self.onlyLiveJsonNodes.includes(d.id)) {
          idleNodes.push(d);
        } else {
          nodearray.push(d);
        }
      });

      livejson.nodes = nodearray;
      const l = [];
      const idleLinks = [];

      livejson.links.forEach((d) => {
        if (self.onlyLiveJsonLinks.includes(d.source.id)) {
          idleLinks.push(d);
        } else {
          l.push(d);
        }
      });
      livejson.nodes.forEach((d) => {
        if (d.id === activeNode.id) {
          d.idleNodes = idleNodes;
          d.idleLinks = idleLinks;
          d.visible = false;
        }
      });
      livejson.links = l;
      self.reDrawSankey(livejson);
    }

    function renderExpandLiveDomSankey(activeNode) {
      const livejson = chartData;
      const orgjson = self.orgJSON;
      const newNodes = livejson.nodes;
      const newLinks = livejson.links;
      if (activeNode.idleNodes) {
        activeNode.idleNodes.forEach((d) => {
          newNodes.push(d);
        });
        activeNode.idleLinks.forEach((d) => {
          newLinks.push(d);
        });
      }

      const nodesFromOrg = [];
      livejson.nodes.forEach((d) => {
        const element = orgjson.nodes.filter((o) => o.id === d.id);
        if (element && element[0]) {
          if (element[0].id === activeNode.id) {
            d.visible = true;
          } else {
            element[0].visible = d.visible;
          }
          element[0].idleNodes = d.idleNodes;
          element[0].idleLinks = d.idleLinks;
          nodesFromOrg.push(element[0]);
        }
      });
      livejson.nodes = nodesFromOrg;
      const linksFromOrg = [];
      livejson.links.forEach((d) => {
        const element = orgjson.links.filter((o) => o.source === d.source.id || o.source.id === d.source.id);
        if (element && element[0]) {
          linksFromOrg.push(element[0]);
        }
      });
      livejson.links = linksFromOrg;
      livejson.nodes = newNodes;
      livejson.links = newLinks;
      self.reDrawSankey(livejson);
    }

    const wheelMenuActive = false;

    function highlightNodeLinksPrestep(node, d) {
      if (self.data.isMobile && wheelMenuActive) {
        openwheel(d, this, d3.pointer(node), node, this.storedElement);
      } else {
        const eventData = {
          type: 'sankey',
          action: 'showInfoNode',
          data: node,
          controller: window.location.pathname
        };
        self.eventDelegatorService.emiter('custom-ngx-event', eventData);
        self.globalDataService.nodeEvent = eventData;
      }
    }
    this.node = node;
  }

  private updateValidationPill(product: any) {
    const productData = this.chartData.nodes.find((item) => item.rid == product.rid);
    productData.validation_status = product.validation_status;
    this.removeValidationRequiredTag(productData);
    this.setUpdatedStatus(productData);
    if (productData.validation_status !== AIValidationStatuses.VALIDATED) {
      this.addValidationPill(this.node, productData.validation_status, product);
    } else {
      this.removeRectColor(productData);
      this.updateNodeTextAligment(productData);
    }
  }

  private setUpdatedStatus(productData: any) {
    this.node
      .filter((d) => d.rid?.replace('#', '') === productData?.rid?.replace('#', ''))
      .each((d: any) => {
        d.validation_status = productData.validation_status;
      });
  }

  removeValidationRequiredTag(productData: any) {
    this.node
      .filter((d) => d.rid?.replace('#', '') === productData?.rid?.replace('#', '')) // find the node
      .select('.validation-tag') // find the pill group
      .remove();
  }

  private removeRectColor(productData: any) {
    this.node
      .filter((d) => d.rid?.replace('#', '') === productData?.rid?.replace('#', ''))
      .select('rect')
      .attr('fill-opacity', (d: any) => (this.isAiNodeAndValidationRequired(d) ? 0.5 : 1))
      .attr('stroke-width', (d: any) => (this.isAiNodeAndValidationRequired(d) ? 1.5 : 0.71))
      .attr('stroke-dasharray', (d: any) => (this.isAiNodeAndValidationRequired(d) ? '5,5' : null));
  }

  private updateNodeTextAligment(productData: any) {
    this.node
      .filter((d) => d.rid?.replace('#', '') === productData?.rid?.replace('#', ''))
      .select('text.text-label')
      .attr('dy', (d: any) => (this.isAiNodeAndValidationRequired(d) ? '-0.2em' : '0.35em')); // shift up if validation

    this.node
      .filter((d) => d.rid?.replace('#', '') === productData?.rid?.replace('#', ''))
      .each((d: any) => {
        d.validation_status = productData.validation_status;
      })
      .select('text.triple-dot')
      .attr('dy', (d: any) => (this.isAiNodeAndValidationRequired(d) ? '-0.2em' : '0.35em'));
  }

  private getValidationPillColor(status: AIValidationStatuses) {
    const validationPillBorder = ColorAliases.BORDER_UNIVERSAL_INVERTED_LIGHT;
    const validationPillBackground =
      status === AIValidationStatuses.PENDING ? ColorAliases.WARNING_CONTAINER : ColorAliases.ERROR_CONTAINER;
    const validationPillColor = status === AIValidationStatuses.PENDING ? ColorAliases.WARNING : ColorAliases.ERROR;

    return { validationPillBorder, validationPillBackground, validationPillColor };
    // validationPillBorder
  }

  private addValidationPill(node, status?: AIValidationStatuses, product?: any) {
    const tagClass = status === AIValidationStatuses.PENDING ? 'validation-tag' : 'rejected-tag';
    const filterFunction = product
      ? (d) => d.rid?.replace('#', '') === product?.rid?.replace('#', '')
      : (d) => this.getRequiredNode(d, status);
    const validation = node.filter(filterFunction).append('g').attr('class', tagClass);
    const { validationPillBorder, validationPillBackground, validationPillColor } = this.getValidationPillColor(status);

    const textColor = ColorAliases.CONTENT;
    const whiteColor = ColorAliases.INVERSE_CONTENT;
    validation
      .append('rect')
      .attr('x', (d) => d.x1 + 20) // offset to the right of node
      .attr('y', (d) => (d.y0 + d.y1) / 2 + 1) // slightly under node label
      .attr('rx', 10) // rounded corners
      .attr('ry', 10)
      .attr('width', 130) // adjust to fit text
      .attr('height', 22)
      .style('fill', validationPillBackground)
      .style('stroke', validationPillBorder) // orange border
      .style('stroke-width', 1.2);

    validation
      .append('circle')
      .attr('cx', (d) => d.x1 + 34) // inside rect
      .attr('cy', (d) => (d.y0 + d.y1) / 2 + 12)
      .attr('r', 6)
      .style('fill', validationPillColor);
    if (status === AIValidationStatuses.PENDING) {
      // orange circle (icon placeholder)
      validation
        .append('circle')
        .attr('cx', (d) => d.x1 + 34) // inside rect
        .attr('cy', (d) => (d.y0 + d.y1) / 2 + 12)
        .attr('r', 6)
        .style('fill', validationPillColor);
    } else {
      validation
        .append('text')
        .attr('x', (d) => d.x1 + 31) // inside rect
        .attr('y', (d) => (d.y0 + d.y1) / 2 + 15)
        .text('✖') // unicode cross mark
        .style('fill', whiteColor) // dark red
        .style('font-size', '8px');
    }

    const pillText = status === AIValidationStatuses.PENDING ? 'Needs validation' : 'Invalidated';

    // text
    validation
      .append('text')
      .attr('x', (d) => d.x1 + 48) // after circle
      .attr('y', (d) => (d.y0 + d.y1) / 2 + 16) // align middle
      .text(pillText)
      .style('fill', textColor)
      .style('font-size', '12px')
      .style('font-weight', '500');
  }

  private createSVG(width: number, height: number): d3.Selection<SVGSVGElement, unknown, HTMLElement, any> {
    return d3
      .select(this.sankey.nativeElement)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('id', 'sankeySvg')
      .attr('transform', 'translate(0,0) scale(1)');
  }

  private createChildGroup(
    svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, any>,
    width: number,
    height: number
  ): d3.Selection<SVGGElement, unknown, HTMLElement, any> {
    return svg
      .append('g')
      .attr('width', () => window.innerWidth)
      .attr('height', () => window.innerHeight)
      .attr('transform', 'translate(20, 60) scale(1)');
  }

  private isAiNodeAndValidationRequired(d) {
    return (
      d.ai_generated &&
      (d.validation_status === AIValidationStatuses.PENDING || d.validation_status === AIValidationStatuses.REJECTED)
    );
  }

  private getRequiredNode(d, status: AIValidationStatuses) {
    return d.ai_generated && d.validation_status === status;
  }

  private createLinksAndNodes(
    child: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    sankey: any
  ): { link: any; node: any } {
    const link = child
      .append('g')
      .attr('class', 'links')
      .attr('fill', ColorAliases.NODE_PRIMARY)
      .attr('stroke', ColorAliases.ON_WARNING)
      .attr('opacity', '0.4') // Set stroke opacity to 0.2
      .attr('stroke-width', '1')
      .attr('stroke-opacity', '1')
      .selectAll('path');

    const node = child
      .append('g')
      .attr('class', 'nodes')
      .attr('font-family', 'sans-serif')
      .attr('font-size', 10)
      .selectAll('g');

    return { link, node };
  }

  menuEvent(value) {
    if (value === 'savepng') {
      this.toastrService.showMessage('Generating png file of sankey graph', 2000);
      this.saveSvg();
    } else {
      this.eventDelegatorService.emiter('custom-ngx-event', {
        type: 'sankey',
        action: value,
        data: this.currentClickNode,
        controller: window.location.pathname
      });
    }
    setTimeout(() => {
      this.storedElement.style.visibility = 'hidden';
    }, 300);
  }

  saveSvg() {
    const clonedDiv = this.sankey.nativeElement?.children[0];
    const g = this.sankey.nativeElement?.children[0].querySelector('g');
    clonedDiv.setAttribute('width', `${+g.getAttribute('width') + 1200}`);
    clonedDiv.setAttribute('height', `${+g.getAttribute('height') + 1800}`);
    saveSvgAsPng(clonedDiv, 'Sankey_Graph.png', {
      scale: 1,
      backgroundColor: '#FFFFFF',
      top: -300,
      left: -300
    });
  }

  setSankeyHeaders(res) {
    if (res.node_legend) {
      for (const i in res.node_legend) {
        const legendObj: Record<string, any> = {};
        if (res.node_legend[i].color && res.node_legend[i].label) {
          legendObj.name = res.node_legend[i].label;
          legendObj.nltype = res.node_legend[i].type;
          legendObj.color = res.node_legend[i].color;
        }

        this.legend_data.push(legendObj);
      }
    }
    if ((res.edge_legend || res.risk_legend) && ['costing', 'supply_risk'].includes(this.activeLayer.layer))
      this.getHeadersForSupplyRisk(res);
    if (res.edge_legend && ['recycled_content', 'eol_rir'].includes(this.activeLayer.layer))
      this.getHeadersForRecycledContent(res);
  }

  getHeadersForRecycledContent(res) {
    // Added Additional data which is not coming from backend, and we need to show labels
    let lObj = { name: '', type: '' };
    lObj.name = 'Industrial Data Objects';
    this.legend_data.push(lObj);

    const appName =
      this.activeLayer.layer === 'eol_rir' ? 'End-of-Life Recycling Input Rate (EoL-RIR)' : 'Recycled Content';
    lObj = { name: appName, type: 'gradient' };
    lObj = { ...lObj, ...res.edge_legend };
    this.legend_data.push(lObj);
  }

  getHeadersForSupplyRisk(res) {
    let lObj = { name: '', type: '' };
    lObj.name = 'Nodes';
    const edgeLegend: Record<string, any> = res.edge_legend || res.risk_legend;
    this.legend_data.push(lObj);
    edgeLegend.forEach((e) => {
      const legendObj: Record<string, any> = {};
      legendObj.type = e.type;
      legendObj.name = e.label;
      legendObj.color = e.color;
      this.legend_data.push(legendObj);
    });
    lObj = { name: 'Edges', type: 'edge' };
    this.legend_data.push(lObj);
  }

  isDrillable() {
    const node = this.currentClickNode;
    if (!node) return;
    return node.type === 'process' || (node.type === 'product' && (node.in_process || node.jump_route || node.route));
  }

  saveSankey(payload) {
    const position = [];
    const { width, height } = this.analyzeService.getViewportSize('sankey');
    this.graph.nodes.forEach((val, key) => {
      position[key] = { x: val.x1, y: val.y1 };
    });
    payload.data.position = position;
    payload.height = height;
    payload.width = width;
    payload.type = 'sankey';
    const schema = {
      header: 'Save as product',
      lines: [],
      formSchema: [
        {
          defaultValue: null,
          description: 'Name',
          fieldtype: 'STRING',
          formtype: 'text',
          key: 'name',
          sortlevel: 10,
          title: 'Name',
          mandatory: true
        },
        {
          defaultValue: null,
          description: 'Description',
          fieldtype: 'STRING',
          formtype: 'textarea',
          key: 'description',
          sortlevel: 20,
          title: 'Description'
        }
      ]
    };

    const dialogRef = this.dialog.open(InlineFormSchemaDialogComponent, {
      panelClass: 'inline-schema',
      data: schema
    });

    const instance = dialogRef.componentInstance;

    instance.submitDataEmitter.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data && data.value.Inputs) {
        Object.assign(payload, data.value.Inputs);
        this.makersiteService.saveSankey(payload).subscribe((res: any) => {
          this.toastrService.show('Saved Successfully', 2000);
          dialogRef?.close();
          this.router.navigate(['/mattermaps/overview/', res.rid]);
        });
      }
    });
  }

  private stretchSankey() {
    setTimeout(() => {
      const { width, height } = this.analyzeService.getViewportSize('sankey');
      d3.select('#sankey svg')?.attr('width', width).attr('height', height);
      this.legend?.setPosition(
        this.legendLeftBottomMargin,
        height - this.legendLeftBottomMargin,
        this.sankey.nativeElement.clientHeight + 32
      );
    }, 500);
  }

  /**
   * Reorders nodes within each layer to minimize link crossings using
   * the barycenter heuristic. Runs alternating left-to-right and
   * right-to-left sweeps, sorting each column by the median position
   * of connected nodes in the adjacent (already-processed) layer.
   */
  private minimizeCrossings(graph: any, passes: number, padding: number): void {
    // ── Build columns from node layers ──
    const maxLayer = Math.max(...graph.nodes.map((n) => n.layer));
    const columns: any[][] = Array.from({ length: maxLayer + 1 }, () => []);
    for (const n of graph.nodes) {
      columns[n.layer].push(n);
    }

    // ── Initial order: use d3-sankey's layout positions ──
    // Sort each column by the y-position d3 assigned, then pack so
    // positions are consistent with array order.
    for (const col of columns) {
      col.sort((a, b) => a.y0 - b.y0);
      this.packColumn(col, col.length > 0 ? col[0].y0 : 0, padding);
    }

    // ── Phase 1: Barycenter ordering ──
    // Each pass sorts columns by the median y-center of connected nodes
    // in the adjacent already-positioned column, then packs to update
    // positions. Ordering and positioning are kept in sync by packing
    // immediately after each column sort.
    for (let pass = 0; pass < passes; pass++) {
      // Left-to-right: sort by median of sources (left neighbors)
      for (let i = 1; i < columns.length; i++) {
        this.sortColumnByAdjacentMedian(columns[i], 'targetLinks', 'source');
        this.packColumn(columns[i], columns[i].length > 0 ? columns[i][0].y0 : 0, padding);
      }
      // Right-to-left: sort by median of targets (right neighbors)
      for (let i = columns.length - 2; i >= 0; i--) {
        this.sortColumnByAdjacentMedian(columns[i], 'sourceLinks', 'target');
        this.packColumn(columns[i], columns[i].length > 0 ? columns[i][0].y0 : 0, padding);
      }
    }

    // ── Phase 2: Crossing-only swap refinement ──
    // Adjacent swap pass that only keeps swaps reducing crossings.
    // Never swaps for value ordering — ordering is determined by
    // barycenter in Phase 1.
    this.reduceSwapCrossings(columns, padding);

    // ── Phase 3: Position optimization (order-preserving) ──
    // Move nodes toward connected partners without changing order.
    this.realignToConnectors(columns, padding);

    // ── Phase 4: Final crossing sweep ──
    // Realignment can reintroduce crossings; one more swap pass.
    this.reduceSwapCrossings(columns, padding);

    // ── Phase 5: Group spacing ──
    // Re-pack each column with extra space between groups of nodes
    // connected to different parents. This visually separates sibling
    // groups while keeping siblings tightly packed.
    const groupPadding = padding;
    for (const col of columns) {
      if (col.length > 0) {
        this.packColumn(col, col[0].y0, padding, groupPadding);
      }
    }

    // ── Phase 6: Re-align to connectors after group spacing ──
    // Group spacing pushes nodes apart uniformly; this pulls each node
    // back toward its connected partners so children stay close to
    // their parents rather than drifting far below.
    this.realignToConnectors(columns, padding, groupPadding);

    // Re-sort link arrays to match new node positions
    for (const n of graph.nodes) {
      n.sourceLinks.sort((a, b) => a.target.y0 - b.target.y0 || a.index - b.index);
      n.targetLinks.sort((a, b) => a.source.y0 - b.source.y0 || a.index - b.index);
    }

    // Normalize: shift all nodes so the topmost node starts at y=0
    const minY = Math.min(...graph.nodes.map((n) => n.y0));
    if (minY !== 0) {
      for (const n of graph.nodes) {
        n.y0 -= minY;
        n.y1 -= minY;
      }
    }
  }

  /**
   * Counts the number of link crossings between two adjacent columns.
   * Two links cross when source S1 is above S2 but target T1 is below T2
   * (or vice-versa). Uses the y-center of each node for comparison.
   */
  private countColumnPairCrossings(leftCol: any[], rightCol: any[]): number {
    const rightSet = new Set(rightCol);
    const links: { sy: number; ty: number }[] = [];
    for (const node of leftCol) {
      for (const link of node.sourceLinks || []) {
        if (rightSet.has(link.target)) {
          links.push({
            sy: (node.y0 + node.y1) / 2,
            ty: (link.target.y0 + link.target.y1) / 2
          });
        }
      }
    }

    let crossings = 0;
    for (let i = 0; i < links.length; i++) {
      for (let j = i + 1; j < links.length; j++) {
        if ((links[i].sy - links[j].sy) * (links[i].ty - links[j].ty) < 0) {
          crossings++;
        }
      }
    }
    return crossings;
  }

  /**
   * Reduces link crossings using adjacent swaps within each column
   * (Sugiyama adjacent-layer swap refinement).
   *
   * Only keeps swaps that strictly reduce crossings. Never swaps
   * for value ordering — node order is determined by the barycenter
   * heuristic. Also tries coordinated swaps with adjacent columns
   * when a single-column swap would reduce overall crossings.
   * Repeats until no improvement or maxRounds reached.
   */
  private reduceSwapCrossings(columns: any[][], padding: number): void {
    const maxRounds = 10;

    for (let round = 0; round < maxRounds; round++) {
      let improved = false;

      for (let c = 0; c < columns.length; c++) {
        const col = columns[c];
        if (col.length < 2) continue;

        for (let i = 0; i < col.length - 1; i++) {
          const crossingsBefore = this.countAdjacentCrossings(columns, c);
          this.swapAdjacentNodes(col, i, padding);
          const crossingsAfter = this.countAdjacentCrossings(columns, c);

          if (crossingsAfter < crossingsBefore) {
            // Swap reduces crossings — keep
            improved = true;
          } else {
            // Swap didn't reduce crossings — revert
            this.swapAdjacentNodes(col, i, padding);

            // Try coordinated swap with neighbor column
            if (this.tryCoordinatedSwap(columns, c, i, padding)) {
              improved = true;
            }
          }
        }
      }

      if (!improved) break;
    }
  }

  /** Counts crossings between column c and its left/right neighbors. */
  private countAdjacentCrossings(columns: any[][], c: number): number {
    let total = 0;
    if (c > 0) total += this.countColumnPairCrossings(columns[c - 1], columns[c]);
    if (c < columns.length - 1) total += this.countColumnPairCrossings(columns[c], columns[c + 1]);
    return total;
  }

  /** Swaps col[i] and col[i+1] positions and array order in-place. Calling twice reverts. */
  private swapAdjacentNodes(col: any[], i: number, padding: number): void {
    const a = col[i];
    const b = col[i + 1];
    const aHeight = a.y1 - a.y0;
    const bHeight = b.y1 - b.y0;
    const topY = a.y0;
    b.y0 = topY;
    b.y1 = topY + bHeight;
    a.y0 = b.y1 + padding;
    a.y1 = a.y0 + aHeight;
    col[i] = b;
    col[i + 1] = a;
  }

  /**
   * Tries a coordinated swap: swaps two nodes in column c AND their
   * uniquely-connected source/target nodes in an adjacent column.
   * This allows value ordering when a single-column swap would create
   * crossings because the connected nodes are in the "wrong" order.
   */
  private tryCoordinatedSwap(columns: any[][], c: number, i: number, padding: number): boolean {
    if (c > 0 && this.tryCoordinatedSwapWithNeighbor(columns, c, i, c - 1, 'targetLinks', 'source', padding)) {
      return true;
    }
    if (
      c < columns.length - 1 &&
      this.tryCoordinatedSwapWithNeighbor(columns, c, i, c + 1, 'sourceLinks', 'target', padding)
    ) {
      return true;
    }
    return false;
  }

  /**
   * Helper: tries swapping col[c][i] and col[c][i+1], AND swapping their
   * uniquely-connected nodes in column nc, all at once. Returns true if
   * the coordinated swap reduces or maintains crossings.
   */
  private tryCoordinatedSwapWithNeighbor(
    columns: any[][],
    c: number,
    i: number,
    nc: number,
    linkProp: string,
    nodeProp: string,
    padding: number
  ): boolean {
    const col = columns[c];
    const neighborCol = columns[nc];
    const above = col[i];
    const below = col[i + 1];

    // Build a Set + index map for the neighbor column (O(1) lookups)
    const neighborSet = new Set(neighborCol);
    const neighborIdx = new Map<any, number>();
    neighborCol.forEach((n, idx) => neighborIdx.set(n, idx));

    // Find connected nodes in the neighbor column for each node
    const aboveNeighbors: any[] = (above[linkProp] || []).map((l) => l[nodeProp]).filter((n) => neighborSet.has(n));
    const belowNeighbors: any[] = (below[linkProp] || []).map((l) => l[nodeProp]).filter((n) => neighborSet.has(n));

    if (aboveNeighbors.length === 0 || belowNeighbors.length === 0) return false;

    // Find the primary neighbor for each (highest link value)
    const aboveNeighbor = aboveNeighbors.reduce(
      (best, n) => ((n.value || 0) > (best.value || 0) ? n : best),
      aboveNeighbors[0]
    );
    const belowNeighbor = belowNeighbors.reduce(
      (best, n) => ((n.value || 0) > (best.value || 0) ? n : best),
      belowNeighbors[0]
    );

    if (aboveNeighbor === belowNeighbor) return false;

    // The neighbors must be adjacent in the neighbor column
    const ni = neighborIdx.get(aboveNeighbor)!;
    const nj = neighborIdx.get(belowNeighbor)!;
    if (ni >= nj || nj - ni !== 1) return false;

    // Count crossings before across all affected column pairs
    const minC = Math.min(c, nc);
    const maxC = Math.max(c, nc);
    const countAffectedCrossings = (): number => {
      let total = 0;
      if (minC > 0) total += this.countColumnPairCrossings(columns[minC - 1], columns[minC]);
      total += this.countColumnPairCrossings(columns[minC], columns[maxC]);
      if (maxC < columns.length - 1) total += this.countColumnPairCrossings(columns[maxC], columns[maxC + 1]);
      return total;
    };

    const crossingsBefore = countAffectedCrossings();

    // Perform both swaps
    this.swapAdjacentNodes(col, i, padding);
    this.swapAdjacentNodes(neighborCol, ni, padding);

    if (countAffectedCrossings() < crossingsBefore) {
      return true;
    }

    // Revert both swaps
    this.swapAdjacentNodes(col, i, padding);
    this.swapAdjacentNodes(neighborCol, ni, padding);
    return false;
  }

  /**
   * Sorts a column of nodes by the median y-center of their connected
   * nodes in the reference column. Uses y-positions for consistency with
   * the positioning phase.
   *
   * When two nodes have the same median (e.g. siblings sharing a parent),
   * ties are broken by the median of their connections in the OPPOSITE
   * direction — so siblings get ordered by where their other links go,
   * reducing crossings on the far side. If that also ties, falls back to
   * preserving relative input order.
   *
   * Nodes with no connections keep their relative position.
   */
  private sortColumnByAdjacentMedian(column: any[], linkProp: string, nodeProp: string): void {
    // Determine the opposite link direction for tie-breaking
    const oppositeLinkProp = linkProp === 'targetLinks' ? 'sourceLinks' : 'targetLinks';
    const oppositeNodeProp = nodeProp === 'source' ? 'target' : 'source';

    const medians = new Map<any, number>();
    const oppositeMedians = new Map<any, number>();

    for (const node of column) {
      // Primary median: from reference direction
      const yCenters: number[] = [];
      for (const link of node[linkProp] || []) {
        const connected = link[nodeProp];
        if (connected) {
          yCenters.push((connected.y0 + connected.y1) / 2);
        }
      }
      if (yCenters.length > 0) {
        yCenters.sort((a, b) => a - b);
        const mid = Math.floor(yCenters.length / 2);
        medians.set(node, yCenters.length % 2 === 1 ? yCenters[mid] : (yCenters[mid - 1] + yCenters[mid]) / 2);
      }

      // Opposite-direction median: for tie-breaking siblings
      const oppCenters: number[] = [];
      for (const link of node[oppositeLinkProp] || []) {
        const connected = link[oppositeNodeProp];
        if (connected) {
          oppCenters.push((connected.y0 + connected.y1) / 2);
        }
      }
      if (oppCenters.length > 0) {
        oppCenters.sort((a, b) => a - b);
        const mid = Math.floor(oppCenters.length / 2);
        oppositeMedians.set(
          node,
          oppCenters.length % 2 === 1 ? oppCenters[mid] : (oppCenters[mid - 1] + oppCenters[mid]) / 2
        );
      }
    }

    // Record input order for stable sort fallback
    const inputOrder = new Map<any, number>();
    column.forEach((n, idx) => inputOrder.set(n, idx));

    column.sort((a, b) => {
      const ma = medians.get(a);
      const mb = medians.get(b);
      if (ma !== undefined && mb !== undefined) {
        if (ma !== mb) return ma - mb;
        // Same median (siblings sharing a parent) — sort by value descending
        const va = a.value || 0;
        const vb = b.value || 0;
        if (va !== vb) return vb - va;
        // Same value — break ties by opposite-direction median
        const oa = oppositeMedians.get(a);
        const ob = oppositeMedians.get(b);
        if (oa !== undefined && ob !== undefined && oa !== ob) return oa - ob;
        // Still tied — preserve input order (stable)
        return inputOrder.get(a)! - inputOrder.get(b)!;
      }
      if (ma === undefined && mb === undefined) return inputOrder.get(a)! - inputOrder.get(b)!;
      if (ma === undefined) return 1;
      return -1;
    });
  }

  /**
   * Repositions nodes toward their connectors' centers without changing
   * the established node order within each column. Delegates to
   * assignColumnPositions with re-sorting disabled.
   */
  private realignToConnectors(columns: any[][], padding: number, groupPadding = 0): void {
    this.assignColumnPositions(columns, padding, false, groupPadding);
  }

  /**
   * Reassigns y0/y1 positions for all nodes in all columns based on
   * their current order, preserving each node's height.
   *
   * Processes columns right-to-left: rightmost columns are positioned
   * first, then each column to the left positions its nodes near the
   * weighted center of their target nodes in the next column. This
   * keeps links more horizontal and avoids sharp angles.
   *
   * When reSort is true (default), columns are re-sorted by ideal
   * position with value tie-breaking. When false, only positions are
   * adjusted without changing node order.
   *
   * Overlaps are resolved by pushing nodes apart symmetrically.
   */
  private assignColumnPositions(columns: any[][], padding: number, reSort = true, groupPadding = 0): void {
    // First pass: position rightmost column compactly from origin
    const lastCol = columns[columns.length - 1];
    if (lastCol && lastCol.length > 0) {
      this.packColumn(lastCol, Math.min(...lastCol.map((n) => n.y0)), padding, groupPadding);
    }

    // Process right-to-left: each node targets the center of mass
    // of its connected nodes in the already-positioned right column
    for (let c = columns.length - 2; c >= 0; c--) {
      const col = columns[c];
      if (col.length === 0) continue;

      // Compute ideal y for each node: center on connected targets
      for (const n of col) {
        const targets = n.sourceLinks?.map((l) => l.target) || [];
        const idealCenter = this.weightedCenter(targets);
        if (idealCenter !== undefined) {
          const height = n.y1 - n.y0;
          n.y0 = idealCenter - height / 2;
          n.y1 = n.y0 + height;
        }
      }

      if (reSort) {
        // Sort strictly by computed position (no tolerance window).
        // Tie-break by value so siblings with the same ideal position
        // get a deterministic, value-prioritized order.
        col.sort((a, b) => a.y0 - b.y0 || (b.value || 0) - (a.value || 0));
      }

      // Resolve overlaps: push nodes apart (with group spacing if provided)
      this.resolveColumnOverlaps(col, padding, groupPadding);
    }

    // Forward pass left-to-right: pull nodes toward their source
    // (left-side) connections. Nodes that already have right-side
    // connections were well-positioned by the R→L pass, so they
    // only get a small nudge. Nodes with only left-side connections
    // get a stronger pull since the R→L pass couldn't position them.
    for (let c = 1; c < columns.length; c++) {
      const col = columns[c];
      if (col.length === 0) continue;

      for (const n of col) {
        const sources = n.targetLinks?.map((l) => l.source) || [];
        const idealCenter = this.weightedCenter(sources);
        if (idealCenter !== undefined) {
          const height = n.y1 - n.y0;
          const currentCenter = (n.y0 + n.y1) / 2;
          // Strong pull (80%) for leaf nodes; light nudge (20%) for
          // nodes already positioned by the R→L pass
          const hasTargets = n.sourceLinks && n.sourceLinks.length > 0;
          const blendFactor = hasTargets ? 0.2 : 0.8;
          const blendedCenter = currentCenter * (1 - blendFactor) + idealCenter * blendFactor;
          n.y0 = blendedCenter - height / 2;
          n.y1 = blendedCenter + height / 2;
        }
      }

      if (reSort) {
        col.sort((a, b) => a.y0 - b.y0 || (b.value || 0) - (a.value || 0));
      }
      this.resolveColumnOverlaps(col, padding, groupPadding);
    }
  }

  /**
   * Returns the weighted y-center of a list of nodes, weighting each
   * node by its height (larger nodes pull more). Returns undefined if
   * the list is empty.
   */
  private weightedCenter(nodes: any[]): number | undefined {
    if (nodes.length === 0) return undefined;
    let totalWeight = 0;
    let weightedY = 0;
    for (const n of nodes) {
      const mid = (n.y0 + n.y1) / 2;
      const w = n.y1 - n.y0 || 1;
      weightedY += mid * w;
      totalWeight += w;
    }
    return weightedY / totalWeight;
  }

  /**
   * Packs a column of nodes sequentially from a starting y position
   * with uniform padding between each node.
   *
   * When groupPadding is provided (> 0), extra space is added between
   * "groups" of sibling nodes. Groups are determined by shared primary
   * connection: nodes sharing the same dominant source or target are
   * siblings and get standard padding; a group boundary gets
   * padding + groupPadding.
   */
  private packColumn(col: any[], startY: number, padding: number, groupPadding = 0): void {
    const gaps = this.computeColumnGaps(col, padding, groupPadding);
    let y = startY;
    for (let i = 0; i < col.length; i++) {
      const n = col[i];
      const height = n.y1 - n.y0;
      n.y0 = y;
      n.y1 = y + height;
      y = n.y1 + gaps[i];
    }
  }

  /**
   * Precomputes the gap after each node in a column. Returns an array
   * of length col.length where gaps[i] is the space after col[i].
   * The last element is just `padding` (unused but keeps indexing simple).
   *
   * Caches `sharesPrimaryConnection` results so group boundaries are
   * computed once per column rather than on every overlap iteration.
   */
  private computeColumnGaps(col: any[], padding: number, groupPadding: number): number[] {
    const gaps: number[] = new Array(col.length).fill(padding);
    if (groupPadding > 0) {
      for (let i = 0; i < col.length - 1; i++) {
        if (!this.sharesPrimaryConnection(col[i], col[i + 1])) {
          gaps[i] = padding + groupPadding;
        }
      }
    }
    return gaps;
  }

  /**
   * Returns true if two nodes share a primary connection — i.e. they
   * have at least one source or target in common. Nodes that share a
   * connection are "siblings" and should be visually grouped together.
   */
  private sharesPrimaryConnection(a: any, b: any): boolean {
    // Check if they share any target (connected to same node on the right)
    const aTargets = new Set((a.sourceLinks || []).map((l) => l.target));
    for (const link of b.sourceLinks || []) {
      if (aTargets.has(link.target)) return true;
    }
    // Check if they share any source (connected to same node on the left)
    const aSources = new Set((a.targetLinks || []).map((l) => l.source));
    for (const link of b.targetLinks || []) {
      if (aSources.has(link.source)) return true;
    }
    return false;
  }

  /**
   * Resolves vertical overlaps in a column by pushing overlapping
   * nodes downward, then shifting the whole column back so the
   * center of mass stays where it was (prevents cumulative drift).
   *
   * Iterates because the drift correction can re-introduce overlaps.
   */
  private resolveColumnOverlaps(col: any[], padding: number, groupPadding = 0): void {
    if (col.length <= 1) return;

    // Precompute gaps once — node order doesn't change during resolution
    const gaps = this.computeColumnGaps(col, padding, groupPadding);

    const maxIterations = 10;
    for (let iter = 0; iter < maxIterations; iter++) {
      // Record center of mass before resolving
      const centerBefore = col.reduce((sum, n) => sum + (n.y0 + n.y1) / 2, 0) / col.length;

      // Push overlapping nodes downward
      let hadOverlap = false;
      for (let i = 1; i < col.length; i++) {
        const overlap = col[i - 1].y1 + gaps[i - 1] - col[i].y0;
        if (overlap > 0) {
          col[i].y0 += overlap;
          col[i].y1 += overlap;
          hadOverlap = true;
        }
      }

      if (!hadOverlap) break;

      // Shift column back so center of mass is preserved
      const centerAfter = col.reduce((sum, n) => sum + (n.y0 + n.y1) / 2, 0) / col.length;
      const drift = centerAfter - centerBefore;
      if (Math.abs(drift) > 0.5) {
        for (const n of col) {
          n.y0 -= drift;
          n.y1 -= drift;
        }
      }

      // Check if drift correction reintroduced overlaps
      let stillOverlapping = false;
      for (let i = 1; i < col.length; i++) {
        if (col[i - 1].y1 + gaps[i - 1] - col[i].y0 > 0.5) {
          stillOverlapping = true;
          break;
        }
      }
      if (!stillOverlapping) break;
    }
  }
}
